<template>
  <div :class="controlClasses" class="mds-form-control mds-date-range-input">
    <label :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <div ref="calendar-wrap" class="date-value" :class="dateValueClasses">
          <template v-if="split === false">
            <input
              ref="date-common-input"
              :value="selectedDate"
              :placeholder="placeholder"
              readonly
              @focus="showCalendar"
            />
          </template>

          <template v-if="split === true">
            с
            <VInputMask
              ref="date-from-input"
              :disabled="disabled"
              :clearable="clearable"
              :placeholder="placeholderDateFrom"
              :mask="$core.utils.date.dateMask"
              :value="internalRange.start?.toLocalString() ?? ''"
              raw
              @focus="onBoundaryFocus('dateFrom')"
              @input="onInput($event, 'dateFrom')"
            >
              <template #icon>
                <VIcon
                  class="calendar-icon"
                  material="calendar_today"
                />
              </template>
            </VInputMask>
            до
            <VInputMask
              ref="date-to-input"
              :disabled="disabled"
              :clearable="clearable"
              :placeholder="placeholderDateTo"
              :mask="$core.utils.date.dateMask"
              :value="internalRange.end?.toLocalString() ?? ''"
              raw
              @focus="onBoundaryFocus('dateTo')"
              @input="onInput($event, 'dateTo')"
            >
              <template #icon>
                <VIcon
                  class="calendar-icon"
                  material="calendar_today"
                />
              </template>
            </VInputMask>
          </template>
        </div>

        <button
          v-if="showClear"
          class="clear"
          type="button"
          tabindex="-1"
          @click.stop="onClear"
        >
          <VIcon material="cancel" />
        </button>

        <VIcon
          v-if="split === false && showClear === false"
          class="calendar-icon"
          material="calendar_today"
        />
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="$slots['helper-text']">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>

      <VPopup
        class="mds-date-range-input-popup"
        :offset="4"
        :target="$refs['calendar-wrap']"
        :visible="calendarVisible"
        @update:visible="onPopupUpdateVisible"
        no-pointer
        @click.stop
      >
        <div class="calendar">
          <VMonthRenderer
            v-for="(_, index) of Array(showMonths)"
            :key="index"
            :first-day-of-week="0"
            :is-first="index === 0"
            :is-in-range="isInRange"
            :is-disabled="isDisabled"
            :is-selected="isSelected"
            :is-last="index + 1 === showMonths"
            :month="dataForMonthRender[index].month"
            :year="dataForMonthRender[index].year"
            @change-month="onChangeMonth"
            @mouse-enter-date="onMouseEnterDate"
            @mouse-leave-date="onMouseLeaveDate"
            @select-day="onSelectDay"
            @wheel.prevent="onMonthScroll"
          />

          <VDateRangePresets
            v-if="showPresets === true"
            class="presets"
            :range="internalRange"
            @select="onPresetSelect"
          />

          <div
            v-if="showManualInput === true"
            class="manual-input"
          >
            <div class="field">
              <label>Дата от</label>
              <VInputMask
                ref="popup-date-from-input"
                :disabled="disabled"
                :clearable="clearable"
                :placeholder="placeholderDateFrom"
                :mask="$core.utils.date.dateMask"
                :value="internalRange.start?.toLocalString() ?? ''"
                raw
                @input="onInput($event, 'dateFrom')"
              />
            </div>

            <div class="field">
              <label>Дата до</label>
              <VInputMask
                ref="popup-date-to-input"
                :disabled="disabled"
                :clearable="clearable"
                :placeholder="placeholderDateTo"
                :mask="$core.utils.date.dateMask"
                :value="internalRange.end?.toLocalString() ?? ''"
                raw
                @input="onInput($event, 'dateTo')"
              />
            </div>

            <button class="mds-btn" @click="onClear">Очистить</button>
          </div>
        </div>
      </VPopup>
    </div>
  </div>
</template>

<style lang="scss" src="./VDateRangeInput.scss" />

<script lang="ts">
import DateSimple   from '@/core/types/DateSimple';
import DateRange    from '@/core/types/DateRange';
import VBaseControl from "@/core/services/VBaseControl";

import VMonthRenderer from "@/lib/components/VMonthRenderer/VMonthRenderer.vue";
import VPopup         from "@/lib/components/VPopup/VPopup.vue";
import VInputMask     from "@/lib/components/VInputMask/VInputMask.vue";
import VIcon          from "@/lib/components/VIcon/VIcon.vue";
import VDateRangePresets from "@/lib/components/VDateRangePresets/VDateRangePresets.vue";
import type { UnwrapNestedRefs } from "vue";

export default defineComponent({
  name: "VDateRangeInput",

  components: { VPopup, VInputMask, VMonthRenderer, VIcon, VDateRangePresets },

  extends: VBaseControl,

  emits: ['input', 'update:modelValue', 'blur'],

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** v-model. DateRange либо null. */
    modelValue: {
      type:    Object as PropType<DateRange | UnwrapNestedRefs<DateRange>>,
      default: null,
    },
    /** Кнопка очистки */
    clearable: {
      type:    Boolean,
      default: false,
    },
    /** Количество месяцев в попапе */
    showMonths: {
      type:    Number,
      default: 2,
    },
    /** Выравнивание текста */
    textAlign: {
      type:    String,
      default: null,
      validator(value: string) {
        return ["left", "center", "right"].includes(value);
      },
    },
    /** Список быстрых периодов («Сегодня», «Эта неделя», …) в календаре */
    showPresets: {
      type:    Boolean,
      default: false,
    },
    /** Ручной ввод границ в календаре: поля «Дата от» / «Дата до» и кнопка «Очистить» */
    showManualInput: {
      type:    Boolean,
      default: false,
    },
    /** Два поля «с … до …» вместо одного поля со всем диапазоном строкой */
    split: {
      type:    Boolean,
      default: false,
    },
  },

  data() {
    return {
      internalRange:   new DateRange() as DateRange,
      /**
       * Режим двух полей: в какую границу календарь положит выбранный день.
       * Календарь один на оба поля, и onSelectDay получает только дату — без этого поля
       * не понять, менять «с» или «до». Ввод с клавиатуры его не использует: onInput
       * и так знает свою границу. Заполняется в onBoundaryFocus. null — режим одной строки
       */
      calendarTarget:  null as 'dateFrom' | 'dateTo' | null,
      preSelectedDate: null as Date | null,
      visibleDate:     DateSimple.today().startOfMonth(),
      calendarVisible: false,
    };
  },

  computed: {
    placeholderDateFrom(): string {
      return this.placeholder === null ? "Дата от" : `${this.placeholder}, от`;
    },

    placeholderDateTo(): string {
      return this.placeholder === null ? "Дата до" : `${this.placeholder}, до`;
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true)             classes.push("clearable");
      classes.push(this.split === true ? "split" : "single");
      return classes;
    },

    selectedDate(): string | null {
      return this.internalRange.isEmpty()
        ? null
        : this.internalRange.getStringRepresentation();
    },

    dateValueClasses(): string[] {
      const classes: string[] = [];
      if (this.calendarVisible === true) classes.push("focus");
      return classes;
    },

    showClear(): boolean {
      return this.clearable === true
        && this.internalRange.isEmpty() === false
        && this.disabled === false
        && this.split === false;
    },

    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      if (this.textAlign  !== null) result["text-align"] = this.textAlign;
      return result;
    },

    dataForMonthRender(): { month: number; year: number }[] {
      return new Array(this.showMonths).fill(null).map((_item, index) => {
        const monthDate = this.visibleDate.addMonths(index);
        return { month: monthDate.getMonth(), year: monthDate.getFullYear() };
      });
    },
  },

  watch: {
    modelValue(newValue: DateRange | null) {
      this.internalRange = newValue instanceof DateRange ? newValue : new DateRange();
    },
  },

  created() {
    this.internalRange = this.modelValue instanceof DateRange ? this.modelValue : new DateRange();
    this.visibleDate   = (this.internalRange.start ?? DateSimple.today()).startOfMonth();
  },

  methods: {
    /**
     * Централизованный эмит изменений.
     * Устанавливает dirty, обновляет internalRange, эмитит наружу и запускает валидацию.
     */
    emitChange(newRange: DateRange | null) {
      this.validation.markDirty();
      this.internalRange = newRange ?? new DateRange();
      this.$emit("input", newRange);
      this.$emit("update:modelValue", newRange);
      this.validate();
    },

    onChangeMonth(data: { direction: 'next' | 'prev' }) {
      if (data.direction === "next") {
        this.visibleDate = this.visibleDate.addMonths(1);
      } else {
        this.visibleDate = this.visibleDate.addMonths(-1);
      }
    },

    onSelectDay(date: Date) {
      const { start: dateFrom, end: dateTo } = this.internalRange;

      // Режим двух полей: меняем только ту границу, для которой открыт календарь (см. calendarTarget),
      // соседнюю не трогаем. Неверный порядок не исправляем перестановкой — о нём говорит валидация
      if (this.calendarTarget === 'dateFrom') {
        this.emitChange(new DateRange(date, dateTo));
        this.hideCalendar();
        return;
      }

      if (this.calendarTarget === 'dateTo') {
        this.emitChange(new DateRange(dateFrom, date));
        this.hideCalendar();
        return;
      }

      if (dateFrom === null || (dateFrom !== null && dateTo !== null)) {
        this.emitChange(new DateRange(date, null));
        return;
      }

      if (date.getTime() < dateFrom.getTime()) {
        this.emitChange(new DateRange(date, dateFrom));
      } else {
        this.emitChange(new DateRange(dateFrom, date));
      }

      if (this.internalRange.start !== null && this.internalRange.end !== null) {
        this.hideCalendar();
      }
    },

    onMouseEnterDate(date: Date) {
      this.preSelectedDate = date;
    },

    onMouseLeaveDate() {
      this.preSelectedDate = null;
    },

    isSelected(date: Date): boolean {
      const { start: dateFrom, end: dateTo } = this.internalRange;
      if (dateFrom !== null && dateTo !== null) {
        const fromTime = dateFrom.getTime();
        const toTime   = dateTo.getTime();
        const dateTime = this.$core.utils.date.toDayStart(date).getTime();
        return fromTime <= dateTime && dateTime <= toTime;
      }
      return this.$core.utils.date.format(dateFrom) === this.$core.utils.date.format(date);
    },

    isInRange(date: Date): boolean {
      const { start: dateFrom, end: dateTo } = this.internalRange;
      if (this.preSelectedDate === null || dateFrom === null || (dateFrom !== null && dateTo !== null)) {
        return false;
      }
      const fromTime = dateFrom.getTime();
      const toTime   = this.preSelectedDate.getTime();
      const dateTime = date.getTime();
      return (fromTime < dateTime && dateTime < toTime) || (fromTime > dateTime && dateTime > toTime);
    },

    isDisabled(date: Date): boolean | null {
      /**
       * Чтобы определить валидность даты — проверяем что она вписывается в рамки,
       * без оглядки на диапазоны.
       */
      if (this.validateMethod !== null) return this.validateMethod(date);
      return null;
    },

    onMonthScroll(event: WheelEvent) {
      const delta = event.deltaY || event.detail || (event as any).wheelDelta;
      this.onChangeMonth({ direction: delta > 0 ? "next" : "prev" });
    },

    onInput(event: any, field: 'dateFrom' | 'dateTo') {
      if (event.fullfilled === false) {
        if (event.masked.length === 0) {
          if (field === "dateFrom") {
            this.emitChange(new DateRange(null, this.internalRange.end));
          } else {
            this.emitChange(new DateRange(this.internalRange.start, null));
          }
        }
        return;
      }

      const newDate = this.$core.utils.date.createFromInputString(event.masked);
      if (field === "dateFrom") {
        this.emitChange(new DateRange(newDate, this.internalRange.end));
      } else {
        // Одна строка: даты вводят подряд — дата раньше начала становится началом, это удобно.
        // Два поля: соседнее поле не трогаем, неверный порядок покажет валидация
        if (this.split === false && this.internalRange.start !== null && newDate < this.internalRange.start) {
          this.emitChange(new DateRange(newDate, this.internalRange.start));
        } else {
          this.emitChange(new DateRange(this.internalRange.start, newDate));
        }
      }

      if (this.internalRange.start !== null && this.internalRange.end !== null) {
        this.hideCalendar();
      }
    },

    onClear() {
      this.emitChange(null);
      this.$emit("blur", null);
      this.toggleCalendar(false);
    },

    /**
     * Фокус в поле границы (режим двух полей): запоминаем, для какой границы открываем
     * календарь (calendarTarget), и открываем его. Без этого клик по дню начинал диапазон
     * заново: открыли «до», выбрали день — он становился началом, а конец стирался
     */
    onBoundaryFocus(field: 'dateFrom' | 'dateTo') {
      this.calendarTarget = field;
      this.showCalendar();
    },

    showCalendar() {
      if (this.disabled === true) return;
      this.validation.markTouched();
      this.toggleCalendar(true);
      window.addEventListener("keydown", this.keyDownListeners);
    },

    hideCalendar() {
      this.toggleCalendar(false);
      this.$emit("blur", null);
      window.removeEventListener("keydown", this.keyDownListeners);
    },

    onPopupUpdateVisible(visible: boolean) {
      if (visible === false) {
        this.hideCalendar();
      }
    },

    toggleCalendar(value: boolean) {
      this.calendarVisible = value;
      if (value === true) {
        this.visibleDate = (this.internalRange.start ?? DateSimple.today()).startOfMonth();
      }
    },

    onPresetSelect(range: DateRange) {
      this.emitChange(range);
      this.hideCalendar();
    },

    isDateDisabled(date: DateSimple): boolean {
      return this.validateMethod !== null
        ? this.validateMethod(date) !== null
        : false;
    },

    isRangeContainsDisabledDate(dateFrom: DateSimple | null, dateTo: DateSimple | null): boolean {
      if (this.validateMethod === null || dateFrom === null || dateTo === null) return false;
      let currentDate = dateFrom;
      while (currentDate <= dateTo) {
        if (this.isDateDisabled(currentDate) === true) return true;
        currentDate = currentDate.addDays(1);
      }
      return false;
    },

    /**
     * Запускает валидацию текущего диапазона через Validation.
     * Доп. бизнес-правило (диапазон содержит недоступные даты) применяем поверх.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      const { start: dateFrom, end: dateTo } = this.internalRange;
      await this.validation.validate({ dateFrom, dateTo });

      if (this.required === true && this.internalRange.isEmpty() === true) {
        this.validation.errors.push(this.$core.utils.validators.MESSAGES.required);
        this.validation.isValid = false;
      }

      if (dateFrom !== null && dateTo !== null && dateFrom.getTime() > dateTo.getTime()) {
        this.validation.errors.push("Дата начала позже даты окончания");
        this.validation.isValid = false;
      }

      if (this.isRangeContainsDisabledDate(dateFrom, dateTo) === true) {
        this.validation.errors.push("Выбранный период содержит недоступные даты");
        this.validation.isValid = false;
      }
    },

    keyDownListeners(event: KeyboardEvent) {
      switch (event.key) {
        case "Tab": {
          const popupDateFromInput = this.$refs["popup-date-from-input"]?.$el?.querySelector("input") ?? null;
          const popupDateToInput   = this.$refs["popup-date-to-input"]?.$el?.querySelector("input") ?? null;
          const input = document.activeElement;

          if (
            (popupDateFromInput !== null && input === popupDateFromInput)
            || (popupDateToInput !== null && input === popupDateToInput)
          ) {
            return;
          }

          if (this.split === false) {
            this.hideCalendar();
          } else {
            const dateToInput = this.$refs["date-to-input"]?.$el?.querySelector("input") ?? null;
            if (dateToInput !== null && input === dateToInput) {
              this.hideCalendar();
            }
          }
          break;
        }
        case "Escape": {
          if (this.calendarVisible === true) {
            const input = document.activeElement as HTMLElement | null;
            if (input && typeof input.blur === "function") input.blur();
            this.hideCalendar();
          }
          break;
        }
      }
    },
  },
});
</script>
